import { useState, useCallback, useEffect, useRef } from 'react';
import { useAiGeneration } from './useAiGeneration';
import { useUpdateAiSpriteMedia } from './useUpdateAiSpriteMedia';
import { getModelById, getNextImageRole, getImageRoleLabel, resolveClientModel } from '../config/aiModels';
import { estimateCreditCost } from '../config/creditPricing';
import { useAuth } from '../contexts/AuthContext';

/**
 * useAiLiteProperties
 *
 * Encapsulates AI layer editing logic for the lite view.
 * Provides prompt editing, reference image management, generation,
 * and result selection — but intentionally locks the model to preserve
 * the template style.
 *
 * @param {object|null} element - The AI canvas element (type === 'ai')
 */
export const useAiLiteProperties = (element) => {
  const spriteId = element?.id || null;
  const aiPayload = element?.componentData?.aiPayload;

  // Embedded generation history from template (if this sprite was loaded from a template)
  const embeddedHistory = element?.componentData?.aiGenerationHistory || null;

  const { user } = useAuth();
  const updateAiSpriteMedia = useUpdateAiSpriteMedia();

  const {
    job,
    isGenerating,
    error: generationError,
    results,
    history,
    showingAllHistory,
    generate,
    stopGeneration,
    selectResult,
    fetchAllHistory,
    collapseHistory,
  } = useAiGeneration(spriteId, embeddedHistory);

  // Prompt state — defaults to the last used prompt
  const [prompt, setPrompt] = useState(aiPayload?.prompt || '');

  // Reference media — defaults to the last used reference media
  const [referenceMediaList, setReferenceMediaList] = useState(aiPayload?.referenceMedia || []);

  // Track which thumbnail is being swapped
  const [swappingKey, setSwappingKey] = useState(null);

  // Only one thumbnail plays at a time
  const [playingKey, setPlayingKey] = useState(null);

  // Locked model — read from the payload, never changed by the user
  const lockedModel = resolveClientModel(aiPayload?.settings) || 'kling-silent';
  const lockedModelConfig = getModelById(lockedModel);

  // Locked settings from the payload
  const lockedSettings = {
    aspectRatio: aiPayload?.settings?.aspectRatio || '16:9',
    outputCount: aiPayload?.settings?.outputCount || 1,
    length: aiPayload?.settings?.length || '5s',
    voiceId: aiPayload?.settings?.voiceId || null,
  };

  // Credit cost (includes duration for per-second types like video/sound)
  const creditCost = estimateCreditCost(lockedModel, { prompt, length: lockedSettings.length, outputCount: lockedSettings.outputCount });
  const credits = user?.credits ?? 0;
  const canAfford = credits >= (creditCost || 0);

  // Sync when element changes
  useEffect(() => {
    setPrompt(aiPayload?.prompt || '');
    setReferenceMediaList(aiPayload?.referenceMedia || []);
  }, [spriteId, aiPayload]);

  // Select a result from current job or history
  const handleSelectResult = useCallback(async (jobId, resultIndex) => {
    const key = `${jobId}-${resultIndex}`;
    setSwappingKey(key);
    try {
      const selection = await selectResult(jobId, resultIndex);
      if (selection) {
        const sourceJob = job?._id === jobId ? job : history.find(h => h._id === jobId);
        const newAiPayload = sourceJob ? {
          prompt: sourceJob.prompt,
          settings: sourceJob.settings,
          referenceMedia: sourceJob.referenceMedia,
        } : null;

        await updateAiSpriteMedia(
          element.id,
          selection.selectedResult.url,
          selection.selectedResult.mediaType,
          selection.media.originalName,
          selection.selectedResult.thumbnailUrl,
          newAiPayload
        );
      }
    } finally {
      setSwappingKey(null);
    }
  }, [selectResult, updateAiSpriteMedia, element?.id, job, history]);

  // Add a reference image
  const handleAddReference = useCallback((url, name, thumbnailUrl) => {
    setReferenceMediaList((prev) => {
      const role = getNextImageRole(lockedModel, prev);
      return [
        ...prev,
        { type: 'image', url, name: name || '', thumbnailUrl, ...(role && { role }) },
      ];
    });
  }, [lockedModel]);

  // Remove a reference image
  const handleRemoveReference = useCallback((index) => {
    setReferenceMediaList((prev) => prev.filter((_, i) => i !== index));
  }, []);

  // Generate with locked model settings
  const handleGenerate = useCallback(() => {
    if (!prompt.trim()) return;
    if (isGenerating) return;

    const settings = { model: lockedModel };

    if (lockedModelConfig?.settings.aspectRatio) {
      settings.aspectRatio = lockedSettings.aspectRatio;
    }
    if (lockedModelConfig?.settings.outputCount) {
      settings.outputCount = lockedSettings.outputCount;
    }
    if (lockedModelConfig?.settings.length) {
      settings.length = lockedSettings.length;
    }
    if (lockedModelConfig?.settings.voices && lockedSettings.voiceId) {
      settings.voiceId = lockedSettings.voiceId;
    }

    const payload = {
      prompt: prompt.trim(),
      settings,
      ...(referenceMediaList.length > 0 && { referenceMedia: referenceMediaList }),
    };

    generate(payload);
    setPrompt('');
    setReferenceMediaList([]);
  }, [prompt, lockedModel, lockedModelConfig, lockedSettings, referenceMediaList, isGenerating, generate]);

  // Derive display status
  const jobStatus = job?.status || (isGenerating ? 'pending' : null);

  // The URL currently active on the canvas sprite
  const activeUrl = element?.componentData?.url || '';

  // History excluding current job
  const currentJobId = job?._id;
  const historyJobs = history.filter((h) => h._id !== currentJobId);

  return {
    // State
    prompt,
    setPrompt,
    referenceMediaList,
    isGenerating,
    generationError,
    jobStatus,
    activeUrl,
    lockedModel,
    lockedModelConfig,
    creditCost,
    canAfford,

    // Results
    job,
    results,
    historyJobs,
    showingAllHistory,
    swappingKey,
    playingKey,
    setPlayingKey,

    // Handlers
    handleGenerate,
    stopGeneration,
    handleSelectResult,
    handleAddReference,
    handleRemoveReference,
    fetchAllHistory,
    collapseHistory,
  };
};
